import { Suspense, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  Divider,
  Stack,
  Typography,
  ButtonBase,
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLess from '@mui/icons-material/ExpandLessOutlined';
import { useTranslation } from 'react-i18next';
import { LazyMarkdown } from '@/components/LazyMarkdown';
import { HELP_LINK_SCHEMES, helpLinkComponents } from '@/components/helpLinkMarkdown';
import { useReleaseNotes, type ReleaseNote } from './useReleaseNotes';

interface ReleaseRowProps {
  release: ReleaseNote;
  installed: boolean;
  defaultExpanded: boolean;
}

function ReleaseRow({ release, installed, defaultExpanded }: ReleaseRowProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box>
      <ButtonBase
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          textAlign: 'left',
          py: 0.75,
          borderRadius: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {release.version}
          </Typography>
          {installed && (
            <Chip
              size="small"
              label={t('settings.releaseNotesInstalled', { defaultValue: 'Installed' })}
            />
          )}
          <Typography variant="body2" color="text.secondary" noWrap>
            {release.title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {release.date}
          </Typography>
          {expanded ? (
            <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} />
          ) : (
            <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />
          )}
        </Box>
      </ButtonBase>

      <Collapse in={expanded} unmountOnExit>
        <Box
          sx={{
            pb: 1,
            color: 'text.secondary',
            fontSize: '0.875rem',
            '& ul': { pl: 2.5, m: 0 },
            '& li': { mb: 0.75 },
            '& p': { mt: 0, mb: 1 },
            '& strong': { color: 'text.primary' },
            '& a': { color: 'primary.main' },
          }}
        >
          <Suspense fallback={null}>
            {/* A note links a change to the help section that now describes it. */}
            <LazyMarkdown components={helpLinkComponents} keepSchemes={HELP_LINK_SCHEMES}>
              {release.body}
            </LazyMarkdown>
          </Suspense>
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * "What's new" for the About card: the running version's release note, with the
 * earlier ones underneath.
 *
 * Renders nothing when this build ships no notes — an empty section is worse
 * than no section.
 */
export function WhatsNew() {
  const { t } = useTranslation();
  const { releases, current, loading } = useReleaseNotes();

  if (loading || releases.length === 0) return null;

  // Only an exact match opens. Without one — a dev build, an ad-hoc SHA deploy,
  // or a release whose note nobody wrote — expanding the nearest older note
  // would present the previous release's news as this version's. The history
  // stays available; it just doesn't volunteer itself as current.
  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('settings.releaseNotes', { defaultValue: "What's new" })}
      </Typography>
      <Stack divider={<Divider flexItem />}>
        {releases.map((release) => (
          <ReleaseRow
            key={release.version}
            release={release}
            installed={release.version === current?.version}
            defaultExpanded={release.version === current?.version}
          />
        ))}
      </Stack>
    </>
  );
}

export default WhatsNew;
